import { Compiler, Injectable } from '@angular/core';
import { Observable, of, concat, forkJoin } from 'rxjs';
import { Router } from "@angular/router";
import { I18nService } from '../services/i18n/i18n.service'
import { ApiRequestService } from '../http/api-request.service'
import { LookupDataService } from '../http/lookupdata.service'
import { CommonUtilty } from '../utilities/commonUtility'
import { AppMetadataHandler } from '../../configs/app-metadata.handler'
import { environment } from 'environments/environment'
import { catchError, map, switchMap, tap, toArray } from 'rxjs/operators';
import { MultiLingualService } from '../services/multi-lingual/multi-lingual.service';
import config from '../../../../configs/health-nbu-transaction.json'
import { NewValidationItem, ValidationUtilService } from '../services';
import { AppConfig } from '../../configs/app.config';
import { MetadataService } from '../http/metadata.service';
import occupationData from "../../../../configs/occupation_list.json"

interface Mappings {
  categoryToOccupations: Record<string, string[]>;
  occupationToClass: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class ThreadsService {

  constructor(private router: Router,
    private i18nService: I18nService,
    public appMetadataHandler: AppMetadataHandler,
    public appConfig: AppConfig,
    public lookupService : LookupDataService,
    private commonUtilty: CommonUtilty,
    private apiRequestService: ApiRequestService,
    private lookUpDataService: LookupDataService,
    private metadataService: MetadataService,
    private multiLingualService: MultiLingualService,
    private validationUtilService: ValidationUtilService,
    private compiler: Compiler) { }

  /**
   * Initializes the application during bootstrap process
   * - Clears compiler cache if enabled
   * - Navigates to landing route
   * - Sets up i18n services
   * - Initializes app configuration
   * - Sets up browser back button prevention
   * 
   * @returns Observable that emits a status object upon completion
   */
  APPLICATIONBOOTSTRAPTHREAD(): Observable<any> {
    let msg = 'Application Bootstrap Started. . .'
    this._startThreadLog(msg)
    return of(true).pipe(
      tap(() => {
        if (!environment['disableCacheClear']) {
          this.compiler.clearCache()
        }
        this.router.navigateByUrl(environment['landingRoute']);
        this.i18nService.init(this.appMetadataHandler.defaultLanguage, this.appMetadataHandler.supportedLanguages);
        this.appMetadataHandler.initializeConfig()
        this.commonUtilty.preventBackButton();
        this.apiRequestService.resetSessionExpiredMsgID()
        this._successThreadLog('Application Bootstrap Completed');
      }),
      catchError(error => {
        this._errorThreadLog(`There is some problem in Application Bootstrap Thread: ${error.message || 'Unknown error'}`);
        return of(false);
      }),
      map(status => ({ status }))
    );
  }

  /**
   * Executes pre-login tasks before user authentication
   * - Resets any session expired messages
   * 
   * @returns Observable that emits a status object upon completion
   */
  PRELOGINTHREAD(): Observable<any> {
    let msg = 'Pre-Login Thread Started. . .'
    this._startThreadLog(msg)
    return of(true).pipe(
      tap(() => {
        this.apiRequestService.resetSessionExpiredMsgID();
        this._successThreadLog('Pre-Login Thread Completed');
      }),
      catchError(error => {
        this._errorThreadLog('There is some problem in Pre-Login Thread');
        return of(false);
      }),
      map(status => ({ status }))
    );
  }

  /**
   * Executes a sequence of post-login initialization tasks
   * - Loads lookups and reference data
   * - Fetches exceptions for validation
   * - Loads configuration files
   * - Sets up API endpoint mappings
   * - Configures language settings
   * - Fetches application settings and validation rules
   * 
   * @returns Observable that concatenates all post-login threads
   */
  POSTLOGINTHREAD(): Observable<any> {
    const handleError = (opName: string) => catchError(err => {
      console.error(`Error during ${opName}:`, err);
      return of(null); // or a default fallback value
    });
  
    const serialOpList: Array<Observable<any>> = [
      this.APIENDPOINTMAPPERSTRATEGY().pipe(handleError('APIENDPOINTMAPPERSTRATEGY')),
      this.SETDEFAULTLANGUAGE().pipe(handleError('SETDEFAULTLANGUAGE')),
      this.FETCHCONFIGJSON().pipe(handleError('FETCHCONFIGJSON')),
      this.FETCHAPPLICATIONTABLE().pipe(handleError('FETCHAPPLICATIONTABLE')),
      this.GETLOGGEDINCOMPANY().pipe(handleError('GETLOGGEDINCOMPANY'))
    ];
  
    const parallelOpList: Array<Observable<any>> = [
      this.GENERATEOCCUPATIONMAPPINGS().pipe(handleError('GENERATEOCCUPATIONMAPPINGS')),
      this.LOOKUPPRELOADSTRATEGY().pipe(handleError('LOOKUPPRELOADSTRATEGY')),
      this.EXCEPTIONSPRELOADSTRATEGY().pipe(handleError('EXCEPTIONSPRELOADSTRATEGY')),
      this.FETCHVALIDATIONCONFIGURATIONFILE().pipe(handleError('FETCHVALIDATIONCONFIGURATIONFILE'))
    ];
  
    const msg = 'Post-Login Thread Started. . .';
    this._startThreadLog(msg);
  
    return concat(...serialOpList).pipe(
      toArray(),
      switchMap(() => forkJoin(parallelOpList)),
      catchError(err => {
        console.error('Unhandled error in POSTLOGINTHREAD:', err);
        return of(null); // fallback or fail silently
      })
    );
  }


  /**
   * Sets the default language for the application
   * - Configures the multi-lingual service with default language
   * 
   * @returns Observable that emits true on success
   */
  SETDEFAULTLANGUAGE(): Observable<any> {
    return of(true).pipe(
      tap(() => {
        let msg = 'SET DEFAULT LANGUAGE Thread Started. . .'
        this._startThreadLog(msg)
        this.multiLingualService.setDefaultLanguage(this.appMetadataHandler.defaultLanguage);
        this.multiLingualService.setLanguage(this.appMetadataHandler.defaultLanguage);
        this._successThreadLog('SET DEFAULT LANGUAGE Thread Completed');
      }),
      catchError(error => {
        this._errorThreadLog('There is some problem in SET DEFAULT LANGUAGE Thread');
        return of(null);
      }),
      map(() => true)
    );
  }

  /**
   * Fetches API endpoint mappings from server
   * - Loads API master data for service endpoint resolution
   * 
   * @returns Observable with the API master data
   */
  APIENDPOINTMAPPERSTRATEGY(): Observable<any> {
    return this.apiRequestService.getAPIMaster().pipe(
      tap(res => {
        let msg = 'API MASTER LOAD Thread Started. . .'
        this._startThreadLog(msg)
        if (res) {
          this._successThreadLog('API MASTER LOAD Thread Completed');
        }
      }),
      catchError(error => {
        this._errorThreadLog('There is some problem in API MASTER LOAD Thread. Invalid Response from Server. Cannot move ahead without this');
        return of(null);
      })
    );
  }

  /**
   * Loads the application configuration from JSON
   * - Sets configuration data in the app metadata handler
   * 
   * @returns Observable that emits a status object on success
   */
  FETCHCONFIGJSON(): Observable<any> {
    return of(true).pipe(
      tap(() => {
        let msg = 'FETCH CONFIG Thread Started. . .'
        this._startThreadLog(msg)
        this.appMetadataHandler.configDataJSON = config;
        this._successThreadLog('FETCH CONFIG Thread Completed');
      }),
      catchError(error => {
        this._errorThreadLog('There is some problem in FETCH CONFIG Thread');
        return of(null);
      }),
      map(() => ({ status: true }))
    );
  }

  /**
   * Preloads lookup data needed for the application
   * - Fetches lookup values based on module configuration
   * 
   * @returns Observable that emits a status object on completion
   */
  LOOKUPPRELOADSTRATEGY(): Observable<any> {
    return of(true).pipe(
      map((val) => {
        let msg = 'Lookup Preload Thread Started. . .'
        this._startThreadLog(msg)
        let lookupList = [...this.appMetadataHandler.preloadLookups[this.appMetadataHandler.entryViewModule]];
        //send list of LookupCd and fetch list of LookupCds
        return this.lookUpDataService.getLookupList(lookupList)
      }),
      tap(res => {
        if (res) {
          this._successThreadLog('Lookup Preload Thread Completed');
        } else {
          this._errorThreadLog('There is some problem in Lookup Preload Thread');
        }
      }),
      catchError(error => {
        this._errorThreadLog('Error in Lookup Preload Thread');
        return of(null);
      }),
      map(() => ({ status: true }))
    )
  }

  /**
   * Fetches validation exceptions from the server
   * - Loads exception data for validation framework
   * - Transforms server exceptions to UI validation format
   * 
   * @returns Observable that emits a status object on completion
   */
  EXCEPTIONSPRELOADSTRATEGY(): Observable<any> {
    return this.validationUtilService.fetchAllExceptions().pipe(
      tap(response => {
        let msg = 'Exceptions Preload Thread Started. . .'
        this._startThreadLog(msg)
        if (this.commonUtilty.isArrayIterable(response)) {
          let exceptionsList = this.commonUtilty.deepCopy(response);
          this.appMetadataHandler.exceptionMasterCache = exceptionsList.filter(exception => {
            return {
              exceptionCd: exception.exceptionCd,
              guid: exception.guid,
              exceptionName: exception.exceptionName,
              type: exception.exceptionType,
              overridable: exception.overRideableFl,
              validationLevel: exception.overrideLevel,
              exceptionMsgDO: exception.exceptionMsgDO
            }
          });
          this.validationUtilService.transformServerExceptionDOtoUIValidationDO();
          this._successThreadLog('Exceptions Preload Thread Completed');
        } else {
          this._errorThreadLog('There is some problem in Exceptions Preload Thread');
        }
      }),
      catchError(error => {
        this._errorThreadLog('Error in Exceptions Preload Thread');
        return of(null);
      }),
      map(() => ({ status: true }))
    );
  }

  /**
   * Fetches the validation configuration Excel file and processes it
   * - Retrieves file path from application table
   * - Downloads and parses the Excel file
   * - Converts Excel data to validation configuration format
   * 
   * @returns Observable that emits status on completion
   */
  FETCHVALIDATIONCONFIGURATIONFILE(): Observable<any> {
   return of(true).pipe(
    switchMap(() => {
      let msg = 'FETCH VALIDATION CONFIGURATION FILE Thread Started. . .'
      this._startThreadLog(msg)
      // Safely access application table values with null checks
      const appTableRes = this.metadataService.applicationTableRes || [];
      const filePathEntry = appTableRes.find(app => app.applicationKey === 'EXCELPATH');
      const fileNameEntry = appTableRes.find(app => app.applicationKey === 'UI_VALIDATION_FILE_NAME');
      
      if (!filePathEntry || !fileNameEntry) {
        this._errorThreadLog('Missing required configuration: EXCELPATH or UI_VALIDATION_FILE_NAME');
        return of(null);
      }
        
      const filePath = filePathEntry.applicationValue;
      const fileName = fileNameEntry.applicationValue;
      
      const payload = {
        imageURL: filePath + fileName
      };
        
      const serviceCd = 'fetchMultipart';
      const url = this.appConfig.baseApiPath + this.apiRequestService.mapperUtil('post', serviceCd);
      return this.validationUtilService.parseExcelFromBinaryResponse(url, payload);
    }),
    tap(jsonData => {
      if (!jsonData || !Array.isArray(jsonData)) {
        this._errorThreadLog('Invalid validation configuration data format');
        return;
      }
      const convertedData: { [key: string]: NewValidationItem[] } = this.validationUtilService.convertExcelToJson(jsonData);
      this.validationUtilService.allValidations = convertedData;
      this._successThreadLog('Validation Configuration File Loaded Successfully');
    }),
    catchError(error => {
      this._errorThreadLog(`Error fetching validation configuration: ${error.message || 'Unknown error'}`);
      console.error('Error details:', error);
      return of(null);
    }),
    map(() => ({ status: true }))
   )
  }


  /**
   * Fetches application table data from server
   * - Retrieves configuration settings needed for the application
   * - This data is required before loading validation configuration
   * 
   * @returns Observable that emits the fetched application table data
   */
  FETCHAPPLICATIONTABLE(): Observable<any> {
    return of(true).pipe(
      switchMap(() => {
        let msg = 'Fetching Application Table Started...';
        this._startThreadLog(msg);
        let pathVariables=[]
        pathVariables.push('ONLINE')
        return this.apiRequestService.getCallWithAPIMapper("getApplicationTypeCd",null,null,pathVariables).pipe(
          map(val => {
            if (Array.isArray(val)) {
              this.metadataService.applicationTableRes = val;
              this._successThreadLog('Application table data fetched successfully');
            } else {
              this._errorThreadLog('Application table data is not in expected format');
              console.warn('Expected array but received:', val);
            }
            return val;
          }),
          catchError(error => {
            this._errorThreadLog(`Error fetching application table: ${error.message || 'Unknown error'}`);
            console.error('Error details:', error);
            return of(null);
          })
        )
      }),
      catchError(error => {
        this._errorThreadLog(`Error fetching application table: ${error.message || 'Unknown error'}`);
        console.error('Error details:', error);
        return of(null);
      })
    );
  }
  public currentCompanyKey: string = "currentCompany";
  public storage: Storage = sessionStorage;
  GETLOGGEDINCOMPANY(): Observable<any> {
    return of(true).pipe(
      switchMap(() => {
        let msg = 'Fetching Application Table Started...';
        this._startThreadLog(msg);
        let pathVariables=[]
        pathVariables.push('ONLINE')
        return this.apiRequestService.getCallWithAPIMapper("getCompanyBasicDetails", null, null, null).pipe(
          map(resp => {
            console.warn("Logged In Company", resp);
            let loggedInCompanyDetails = {
              "companyNum": null,
              "companyName": null,
              "stateCd": null,
              "cityCd": null,
              "postCode": null,
              "contactPersonName": null,
              "companyType": null,
              "companyCd": null,
              "companyEmail": null,
              "companyAddressLine1": null,
              "companyAddressLine2": null
            }
            if (resp) {
              loggedInCompanyDetails.companyNum = resp.companyNum;
              loggedInCompanyDetails.companyName = resp.companyName;
              loggedInCompanyDetails.stateCd = resp.stateCd;
              loggedInCompanyDetails.cityCd = resp.cityCd;
              loggedInCompanyDetails.postCode = resp.postCode;
              loggedInCompanyDetails.contactPersonName = resp.contactPersonName;
              loggedInCompanyDetails.companyType = resp.companyType;
              loggedInCompanyDetails.companyCd = resp.companyCd;
              loggedInCompanyDetails.companyEmail = resp.companyEmail;
              loggedInCompanyDetails.companyAddressLine1 = resp.companyAddressLine1;
              loggedInCompanyDetails.companyAddressLine2 = resp.companyAddressLine2;
              this.appMetadataHandler.baseCurrencyPREFIX = resp.currencyCd;
              this.appMetadataHandler.GLOBAL_DATE_FORMAT_ANGULAR = resp.dateFormatCd || "dd MMM yyyy";
              this.appMetadataHandler.GLOBAL_DATE_FORMAT_MOMENT = resp.dateFormatCd || "dd MMM yyyy";
            }
            this.storage.setItem(this.currentCompanyKey, JSON.stringify(loggedInCompanyDetails));
            return loggedInCompanyDetails;
          }),
          catchError(error => {
            this._errorThreadLog(`Error fetching Logged in Comapny Info: ${error.message || 'Unknown error'}`);
            console.error('Error details:', error);
            return of(null);
          })
        )
      }),
      catchError(error => {
        this._errorThreadLog(`Error fetching Logged in Comapny Info: ${error.message || 'Unknown error'}`);
        console.error('Error details:', error);
        return of(null);
      })
    );
  }

  /**
 * Generates two mappings based on occupation data:
 *
 * 1. categoryToOccupations: A map from each occupation category to a list of unique occupations.
 * 2. occupationToClass: A map from each occupation to its corresponding classification.
 *
 * It filters out entries with missing category or occupation values,
 * trims whitespace, and ensures occupations are not duplicated in each category.
 *
 * @param data - Array of occupation entries with category, occupation, and class.
 * @returns An object containing both category-to-occupations and occupation-to-class maps.
 */
  GENERATEOCCUPATIONMAPPINGS() : Observable<Mappings> {
    let data: any[] = occupationData['Occupation Class'];
    const categoryToOccupations: Record<string, Set<string>> = {};
    const occupationToClass: Record<string, string> = {};
  
    for (const entry of data) {
      const category = entry['Industry (Eng)'];
      const occupation = entry['Detail Job Nature - Eng '];
      const occClass = entry['Class'];
      if (!category || !occupation) continue;
      if (!categoryToOccupations[category]) {
        categoryToOccupations[category] = new Set();
      }
      categoryToOccupations[category].add(occupation);
      occupationToClass[occupation] = occClass || '';
    }
    
    const categoryToOccupationsArray: Record<string, string[]> = {};
    for (const [category, occupationsSet] of Object.entries(categoryToOccupations)) {
      categoryToOccupationsArray[category] = Array.from(occupationsSet);
    }
  
    return of(
      {
        categoryToOccupations: categoryToOccupationsArray,
        occupationToClass,
      }
    ).pipe(
      tap((data)=>this.lookUpDataService.occupationMappings = {...data})
    )
  }
  

  /**
   * Logs the start of a thread execution
   * @param msg Message to log
   */
  _startThreadLog(msg) {
    console.log(`${msg}`, 'color: white; background-color: orange; padding: 2px 5px; border-radius: 2px');
  }

  /**
   * Logs successful completion of a thread
   * @param msg Success message to log
   */
  _successThreadLog(msg) {
    console.log(`${msg}`, 'color: white; background-color: green; padding: 2px 5px; border-radius: 2px');
  }

  /**
   * Logs error during thread execution
   * @param msg Error message to log
   */
  _errorThreadLog(msg) {
    console.log(`${msg}`, 'color: white; background-color: red; padding: 2px 5px; border-radius: 2px');
  }
}
